import React, { useState, Component } from 'react';
import { SmartphoneIcon, MonitorIcon } from 'lucide-react';
// Mobile Components
import { MobileLayout } from './components/shared/MobileLayout';
import { SplashScreen } from './components/mobile/SplashScreen';
import { LoginScreen } from './components/mobile/LoginScreen';
import { HomeScreen } from './components/mobile/HomeScreen';
import { AreaSelection } from './components/mobile/AreaSelection';
import { GarmentForm } from './components/mobile/GarmentForm';
import { ConfirmationScreen } from './components/mobile/ConfirmationScreen';
import { SyncScreen } from './components/mobile/SyncScreen';
import { HistoryScreen } from './components/mobile/HistoryScreen';
import { ProfileScreen } from './components/mobile/ProfileScreen';
// Web Components
import { WebSidebar } from './components/shared/WebSidebar';
import { WebLogin } from './components/web/WebLogin';
import { Dashboard } from './components/web/Dashboard';
import { CollectionsTable } from './components/web/CollectionsTable';
import { Catalogs } from './components/web/Catalogs';
import { Reports } from './components/web/Reports';
import { AuditLog } from './components/web/AuditLog';
type AppMode = 'mobile' | 'web';
type MobileScreen = 'splash' | 'login' | 'home' | 'area' | 'form' | 'confirmation' | 'sync' | 'history' | 'profile';
type WebScreen = 'login' | 'dashboard' | 'collections' | 'catalogs' | 'reports' | 'audit';
export function App() {
  const [mode, setMode] = useState<AppMode>('mobile');
  // Mobile State
  const [mobileScreen, setMobileScreen] = useState<MobileScreen>('splash');
  const [mobileTab, setMobileTab] = useState<'home' | 'history' | 'sync' | 'profile'>('home');
  const [selectedArea, setSelectedArea] = useState<string>('');
  // Web State
  const [webScreen, setWebScreen] = useState<WebScreen>('login');
  // Mobile Handlers
  const handleMobileTabChange = (tab: 'home' | 'history' | 'sync' | 'profile') => {
    setMobileTab(tab);
    setMobileScreen(tab);
  };
  const renderMobileScreen = () => {
    switch (mobileScreen) {
      case 'splash':
        return <SplashScreen onNext={() => setMobileScreen('login')} data-id="element-0" />;
      case 'login':
        return <LoginScreen onLogin={() => {
          setMobileScreen('home');
          setMobileTab('home');
        }} data-id="element-1" />;
      case 'home':
        return <MobileLayout activeTab="home" onTabChange={handleMobileTabChange} data-id="element-2">
            <HomeScreen onNewCollection={() => setMobileScreen('area')} onNavigate={tab => handleMobileTabChange(tab)} data-id="element-3" />
          </MobileLayout>;
      case 'area':
        return <AreaSelection onBack={() => setMobileScreen('home')} onSelectArea={area => {
          setSelectedArea(area);
          setMobileScreen('form');
        }} data-id="element-4" />;
      case 'form':
        return <GarmentForm areaName={selectedArea} onBack={() => setMobileScreen('area')} onSave={() => setMobileScreen('confirmation')} data-id="element-5" />;
      case 'confirmation':
        return <ConfirmationScreen onNew={() => setMobileScreen('area')} onHome={() => setMobileScreen('home')} onSync={() => handleMobileTabChange('sync')} data-id="element-6" />;
      case 'sync':
        return <MobileLayout activeTab="sync" onTabChange={handleMobileTabChange} data-id="element-7">
            <SyncScreen data-id="element-8" />
          </MobileLayout>;
      case 'history':
        return <MobileLayout activeTab="history" onTabChange={handleMobileTabChange} data-id="element-9">
            <HistoryScreen data-id="element-10" />
          </MobileLayout>;
      case 'profile':
        return <MobileLayout activeTab="profile" onTabChange={handleMobileTabChange} data-id="element-11">
            <ProfileScreen onLogout={() => setMobileScreen('login')} data-id="element-12" />
          </MobileLayout>;
      default:
        return <div data-id="element-13">Screen not found</div>;
    }
  };
  // Web Handlers
  const renderWebScreen = () => {
    if (webScreen === 'login') {
      return <WebLogin onLogin={() => setWebScreen('dashboard')} data-id="element-14" />;
    }
    return <WebSidebar activeTab={webScreen as any} onTabChange={tab => setWebScreen(tab)} onLogout={() => setWebScreen('login')} data-id="element-15">
        {webScreen === 'dashboard' && <Dashboard data-id="element-16" />}
        {webScreen === 'collections' && <CollectionsTable data-id="element-17" />}
        {webScreen === 'catalogs' && <Catalogs data-id="element-18" />}
        {webScreen === 'reports' && <Reports data-id="element-19" />}
        {webScreen === 'audit' && <AuditLog data-id="element-20" />}
      </WebSidebar>;
  };
  return <div className="min-h-screen w-full bg-gray-200 flex items-center justify-center relative font-sans" data-id="element-21">
      {/* Mode Switcher */}
      <div className="fixed top-4 right-4 z-50 bg-white rounded-full shadow-lg border border-gray-200 p-1 flex" data-id="element-22">
        <button onClick={() => setMode('mobile')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${mode === 'mobile' ? 'text-white' : 'text-gray-500 hover:bg-gray-100'}`} style={mode === 'mobile' ? {
        backgroundColor: '#134073'
      } : {}} data-id="element-23">
          <SmartphoneIcon className="w-4 h-4" data-id="element-24" />
          Móvil (Recolector)
        </button>
        <button onClick={() => setMode('web')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${mode === 'web' ? 'text-white' : 'text-gray-500 hover:bg-gray-100'}`} style={mode === 'web' ? {
        backgroundColor: '#134073'
      } : {}} data-id="element-25">
          <MonitorIcon className="w-4 h-4" data-id="element-26" />
          Web (Supervisor)
        </button>
      </div>

      {/* App Container */}
      {mode === 'mobile' ? <div className="w-full max-w-[390px] h-[844px] bg-white rounded-[3rem] shadow-2xl overflow-hidden border-[8px] border-gray-900 relative" data-id="element-27">
          {/* Notch simulation */}
          <div className="absolute top-0 inset-x-0 h-6 bg-transparent z-50 flex justify-center" data-id="element-28">
            <div className="w-32 h-6 bg-gray-900 rounded-b-3xl" data-id="element-29"></div>
          </div>
          {renderMobileScreen()}
        </div> : <div className="w-full h-screen bg-white overflow-hidden" data-id="element-30">
          {renderWebScreen()}
        </div>}
    </div>;
}